import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

// Track open modal count so only the topmost modal responds to Escape
let openModalCount = 0;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /**
   * Whether clicking the backdrop dismisses the modal. Defaults to `true`, so
   * every existing caller keeps the behaviour it has always had.
   *
   * Set `false` for a modal holding work that a stray click must not destroy —
   * the bulk AI enrich tool, where a dismissed dialog throws away results that
   * cost real money to produce. Those callers still close via the ✕ and Escape,
   * both of which route through `onClose` and so through any guard the host
   * puts there; the backdrop is singled out because it is the one exit nobody
   * aims for.
   */
  dismissOnBackdrop?: boolean;
  /**
   * Small controls rendered in the header, between the title and the ✕ — e.g.
   * a Copy link icon button. The title stays a plain string.
   */
  headerActions?: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  dismissOnBackdrop = true,
  headerActions,
}: ModalProps) {
  const layerRef = useRef(0);
  const [layer, setLayer] = useState(0);

  useEffect(() => {
    if (isOpen) {
      openModalCount++;
      layerRef.current = openModalCount;
      setLayer(openModalCount);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (isOpen) {
        openModalCount--;
      }
      if (openModalCount === 0) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && layerRef.current === openModalCount) {
        e.stopImmediatePropagation();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-[90vw]',
  };

  const zIndex = 50 + layer;

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto" style={{ zIndex }}>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={dismissOnBackdrop ? onClose : undefined}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        {/*
          No `transform` class here. A non-`none` transform makes this element
          the containing block for `position: fixed` DESCENDANTS, which traps
          any child that means to cover the viewport — the markdown editor's
          fullscreen layer (`.w-md-editor-fullscreen`, `position: fixed; inset:
          0`) got clipped to the panel instead of filling the screen. Nothing
          here applies a translate/scale utility, so the class was inert anyway.
        */}
        <div
          className={clsx(
            'relative w-full bg-white rounded-lg shadow-xl transition-all',
            sizeClasses[size]
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <div className="flex items-center gap-3">
              {headerActions}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="p-4 max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}
